import { CSSProperties, makeStyles } from '@material-ui/styles'
import { SnackbarProvider } from 'notistack'
import React, { FC, useCallback, useMemo, useState } from 'react'

import { ToastContext, ToastId } from '../lib/toasts/context'

import ErrorToastIcon from '../img/icons/error-toast.svg'
import InfoToastIcon from '../img/icons/info-toast.svg'
import SuccessToastIcon from '../img/icons/success-toast.svg'
import WarningToastIcon from '../img/icons/warning-toast.svg'

const toastBase: CSSProperties = {
  display: 'flex',
  borderRadius: '8px',
  fontSize: '16px',
  fontFamily: 'Gotham, sans-serif',
  fontWeight: 400,
  color: '#000',
  '& > div:first-child': {
    gap: '16px',
    width: '85%',
  },
  '& > div:last-child': {
    padding: '0',
  },
}

const useStyles = makeStyles(() => ({
  containerRoot: {
    zIndex: 999999999,
    '@media(min-width: 600px)': {
      maxWidth: '680px',
    },
  },
  variantSuccess: {
    ...toastBase,
    backgroundColor: '#C7E3D0',
    border: '1px solid #5AAD75',
    flexDirection: 'row',
  },
  variantError: {
    ...toastBase,
    backgroundColor: '#F5BABA',
    border: '1px solid #E64B37',
    flexDirection: 'row',
  },
  variantWarning: {
    ...toastBase,
    backgroundColor: '#FFF7D3',
    border: '1px solid #FFD000',
    flexDirection: 'row',
  },
  variantInfo: {
    ...toastBase,
    backgroundColor: '#E3F0F9',
    border: '1px solid #4F7D9C',
    flexDirection: 'row',
  },
}))

const CustomInfoIcon = () => <img src={InfoToastIcon} alt="Info Icon" className="h-6 w-6" />

const CustomSuccessIcon = () => (
  <img src={SuccessToastIcon} alt="Success Icon" className="h-6 w-6" />
)

const CustomErrorIcon = () => <img src={ErrorToastIcon} alt="Error Icon" className="h-6 w-6" />

const CustomWarningIcon = () => (
  <img src={WarningToastIcon} alt="Warning Icon" className="h-6 w-6" />
)

const ToastContextProvider: FC = ({ children }) => {
  const [toasts, setToasts] = useState<ToastId[]>(() => [])
  const [dismissed, setDismissed] = useState<ToastId[]>(() => [])

  const isToastShown = useCallback((toastId: ToastId) => toasts.includes(toastId), [toasts])

  const isToastDismissed = useCallback(
    (toastId: ToastId) => dismissed.includes(toastId),
    [dismissed],
  )

  // use single hook to memoize actions without deps
  const actions = useMemo(
    () => ({
      showToast: (toastId: ToastId) => {
        setToasts((currentToasts) => {
          if (currentToasts.includes(toastId)) {
            return currentToasts
          }
          return [...currentToasts, toastId]
        })
      },

      hideToast: (toastId: ToastId) => {
        setToasts((currentToasts) => {
          if (!currentToasts.includes(toastId)) {
            return currentToasts
          }
          const newToasts = [...currentToasts]
          newToasts.splice(newToasts.indexOf(toastId), 1)
          return newToasts
        })
      },

      dismissToast: (toastId: ToastId) => {
        setDismissed((currentDismissed) => {
          if (currentDismissed.includes(toastId)) {
            return currentDismissed
          }
          return [...currentDismissed, toastId]
        })
      },
    }),
    [],
  )

  const classes = useStyles()

  return (
    <ToastContext.Provider
      value={{
        ...actions,
        toasts,
        dismissed,
        isToastShown,
        isToastDismissed,
      }}
    >
      <SnackbarProvider
        maxSnack={4}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        classes={{
          containerRoot: classes.containerRoot,
          variantSuccess: classes.variantSuccess,
          variantError: classes.variantError,
          variantWarning: classes.variantWarning,
          variantInfo: classes.variantInfo,
        }}
        iconVariant={{
          info: <CustomInfoIcon />,
          success: <CustomSuccessIcon />,
          error: <CustomErrorIcon />,
          warning: <CustomWarningIcon />,
        }}
      >
        {children}
      </SnackbarProvider>
    </ToastContext.Provider>
  )
}

export default ToastContextProvider
